/**
 * The host's half of the applet data pipe, shared by web and native.
 *
 * An applet frame has an opaque origin and no cookies, so it can't call our API.
 * Its host relays the calls instead, as a plain pipe: two fixed calls on routes
 * the host derives from what it framed, with bodies and responses passed through
 * uninterpreted. Everything that gives them meaning lives in the frame's shell
 * and the API, which deploy together. Native host code updates over the air
 * only on binaries that match `main`'s runtime version, so older binaries keep
 * this half; it has to outlive changes to that meaning.
 */

import { isAxiosError } from "axios";
import {
  APPLET_HOST_MESSAGE_SOURCE,
  APPLET_PIPE_PARAM,
  type AppletDataRequestMessage,
  type AppletDataResponseMessage,
  appletDataRequestMessage,
} from "@/types/appletMessages.ts";

/**
 * The routes beside an applet frame's own: an owner's or grantee's, or a link
 * holder's. Data calls authorize exactly as the frame did.
 */
export type AppletDataBase = `/api/a/artifacts/${string}` | `/api/p/shared/a/${string}`;

export type AppletDataCall =
  | { method: "post"; url: `${AppletDataBase}/data/ops`; body: unknown }
  | { method: "get"; url: `${AppletDataBase}/data`; query: Record<string, string> };

const PREVIEW_SUFFIX = "/appletPreview";
const SHARED_DATA_BASE = /^\/api\/(?:a\/artifacts|p\/shared\/a)\/[^/]+$/;

/**
 * Where a host sends its frame's data calls, or null when it frames something it
 * can't relay them for.
 *
 * An owned applet uses its account-scoped routes. A shared one uses the routes
 * beside the src its host was handed, so a link holder's calls go through the
 * token and a grantee's through their grant. Matched as a string, with no `URL`
 * parsing, so it needs nothing from Hermes beyond the language itself.
 */
export function appletDataBase({
  artifactId,
  sharedPreviewSrc,
}: {
  artifactId: string;
  sharedPreviewSrc?: string;
}): AppletDataBase | null {
  if (sharedPreviewSrc === undefined) return `/api/a/artifacts/${encodeURIComponent(artifactId)}`;
  const path = sharedPreviewSrc.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "").split(/[?#]/, 1)[0];
  if (!path?.endsWith(PREVIEW_SUFFIX)) return null;
  const base = path.slice(0, -PREVIEW_SUFFIX.length);
  return SHARED_DATA_BASE.test(base) ? (base as AppletDataBase) : null;
}

/**
 * The frame URL, marked as hosted by something that relays its data calls.
 */
export const withAppletPipeParam = (src: string): string =>
  `${src}${src.includes("?") ? "&" : "?"}${APPLET_PIPE_PARAM}=1`;

/**
 * A `data.request` from the frame, or null for anything else. The frame runs
 * untrusted code, so this is the whole of what a host accepts from it.
 */
export function parseAppletDataRequest(data: unknown): AppletDataRequestMessage | null {
  const parsed = appletDataRequestMessage.safeParse(data);
  return parsed.success ? parsed.data : null;
}

/**
 * Make the call a `data.request` names, and answer it whatever happens.
 *
 * An error status is an answer like any other, for the frame to interpret. Only
 * a call that got no response at all answers with status 0.
 */
export async function answerAppletDataRequest(
  base: AppletDataBase,
  request: AppletDataRequestMessage,
  send: (call: AppletDataCall) => Promise<{ status: number; data: unknown }>,
): Promise<AppletDataResponseMessage> {
  const reply = (status: number, body: unknown): AppletDataResponseMessage => ({
    source: APPLET_HOST_MESSAGE_SOURCE,
    type: "data.response",
    requestId: request.requestId,
    status,
    body,
  });
  const call: AppletDataCall =
    request.op === "ops"
      ? { method: "post", url: `${base}/data/ops`, body: request.body }
      : { method: "get", url: `${base}/data`, query: request.query ?? {} };
  try {
    const response = await send(call);
    return reply(response.status, response.data);
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return reply(error.response.status, error.response.data);
    }
    return reply(0, null);
  }
}
